import { DateTime } from "luxon";
import api from "./api";
import { getSystemInformation } from "../utils/systemUtils";

const toNullable = (value) => (value === undefined ? null : value);

class DeviceInfoService {
    constructor({ writeUrl }) {
        this.writeUrl = writeUrl;
    }

    buildDeviceInfoPayload = ({
        eventType,
        actionType,
        source,
        user,
        systemInformation,
        visit,
        error
    }) => {
        const resolvedSystemInformation = systemInformation || getSystemInformation() || {};
        const account = user?.account;

        return {
            eventDateTime: DateTime.now().toISO(),
            eventType,
            actionType: toNullable(actionType),
            source: toNullable(source),
            account: toNullable(account),
            organizationId: toNullable(user?.currentOrganizationId),
            staffId: toNullable(user?.staffId || user?.staff?.staffId),
            platformType: toNullable(resolvedSystemInformation.platformType),
            deviceType: toNullable(resolvedSystemInformation.deviceType),
            browserName: toNullable(resolvedSystemInformation.browserName),
            browserVersion: toNullable(resolvedSystemInformation.browserVersion),
            osName: toNullable(resolvedSystemInformation.osName),
            network: toNullable(resolvedSystemInformation.network),
            appointmentId: toNullable(visit?.appointmentId),
            evvVisitId: toNullable(visit?.evvVisitId),
            syncId: toNullable(visit?.syncId),
            clientId: toNullable(visit?.clientId),
            beginActualDateTime: toNullable(visit?.beginActualDateTime),
            startLatitude: toNullable(visit?.startLatitude),
            startLongitude: toNullable(visit?.startLongitude),
            endActualDateTime: toNullable(visit?.endActualDateTime),
            endLatitude: toNullable(visit?.endLatitude),
            endLongitude: toNullable(visit?.endLongitude),
            errorName: toNullable(error?.name),
            errorMessage: toNullable(error?.stack)
        };
    }

    trackDeviceInfo = ({
        eventType,
        actionType,
        source,
        user,
        systemInformation,
        visit,
        error
    }) => {
        const payload = this.buildDeviceInfoPayload({
            eventType,
            actionType,
            source,
            user,
            systemInformation,
            visit,
            error
        });

        const request = {
            resources: [payload]
        };

        return api.post(this.writeUrl, { json: request })
            .catch((trackingError) => {
                console.log('Error writing device info telemetry');
                console.log(trackingError);
                return null;
            });
    }

    /**
     * Track multiple visits in a single API call
     * Used for batch telemetry tracking before synchronization
     *
     * @param {Object} params - Tracking parameters
     * @param {string} params.eventType - Type of event being tracked
     * @param {string} params.actionType - Action being performed
     * @param {string} params.source - Source of the tracking call
     * @param {Object} params.user - User information
     * @param {Object} params.systemInformation - System information (optional)
     * @param {Array} params.visits - Array of visits to track
     * @returns {Promise} Promise that resolves when tracking is complete
     */
    trackMultipleVisits = ({
        eventType,
        actionType,
        source,
        user,
        systemInformation,
        visits = []
    }) => {
        if (!visits || visits.length === 0) {
            return Promise.resolve(null);
        }

        const payloads = visits.map(visit =>
            this.buildDeviceInfoPayload({
                eventType,
                actionType,
                source,
                user,
                systemInformation,
                visit,
                error: null
            })
        );

        const request = {
            resources: payloads
        };

        return api.post(this.writeUrl, { json: request })
            .catch((trackingError) => {
                console.log('Error writing device info telemetry for multiple visits');
                console.log(trackingError);
                return null;
            });
    }
}

export default DeviceInfoService;
